import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { TypingIndicator } from "./TypingIndicator";
import { JobResultCard } from "./JobResultCard";
import { useChat } from "../../hooks/useChat";
import { createJob, getJobById, pollJobStatus } from "../../api/jobs";
import { JobType } from "../../types";
import { useDispatch } from "react-redux";
import insightsApi from "../../api/insights";
import {
  Bot,
  Video,
  FilePlus,
  ClipboardList,
  Lightbulb,
  Plus,
  XCircle,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../common";

// Types for job display
interface PendingJob {
  jobId: string;
  jobNumber: string;
  imagePreview: string;
  status: "processing" | "completed" | "failed";
  jobData?: any;
  sessionId?: string;
  errorMessage?: string;
}

// Action button component matching mockup style
interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

const ActionButton = ({ icon, label, onClick }: ActionButtonProps) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-4 py-2.5 bg-[#1a2b4b]/40 border border-[#2d4a7c] rounded-xl text-blue-100 hover:bg-[#2d4a7c]/60 transition-all text-sm font-medium"
  >
    <span className="text-blue-400">{icon}</span>
    {label}
  </button>
);

export const ChatInterface: React.FC = () => {
  const navigate = useNavigate();

  const {
    currentSession,
    messages,
    sending,
    error,
    sendMessage,
    startNewChat,
    closeSession,
  } = useChat();
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null);
  const [mode, setMode] = useState<"create" | "query" | "help" | null>(null);
  const [lastJobId, setLastJobId] = useState<string | null>(null);
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Clear pendingJobs when session changes
  useEffect(() => {
    setPendingJobs([]);
    setUploadedImagePreview(null);
    setMode(null);
  }, [currentSession?.id]);

  const handleStartNewChat = () => {
    setPendingJobs([]);
    setMode(null);
    startNewChat();
  };

  const handleEndConversation = async () => {
    if (!currentSession) return;
    const confirmed = window.confirm(
      "End this conversation? You can view it later but won't be able to send new messages."
    );
    if (confirmed) {
      await closeSession(currentSession.id);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleModeSelect = async (selectedMode: "create" | "query" | "help" | "analyze") => {
    setMode(selectedMode === "analyze" ? "create" : selectedMode);
    let messageText = "";
    if (selectedMode === "analyze") {
      messageText = "I want to analyze my video";
    } else if (selectedMode === "create") {
      messageText = "I want to create a job";
    } else if (selectedMode === "query") {
      messageText = "I want to check job status";
    } else {
      messageText = "I need help explaining a result";
    }
    await sendMessage(messageText, currentSession?.id);
  };

  const handleSendMessage = async (content: string) => {
    await sendMessage(content, currentSession?.id);
  };

  const handleUploadFile = async (file: File) => {
    const lastMessage = messages[messages.length - 1];
    const jobType = lastMessage?.metadata?.job_type as JobType;
    if (!jobType) {
      await sendMessage("What type of analysis?", currentSession?.id);
      return;
    }
    setUploading(true);
    const imagePreviewUrl = URL.createObjectURL(file);
    setUploadedImagePreview(imagePreviewUrl);

    try {
      const llmProvider = currentSession?.llm_provider;
      const llmModel = currentSession?.llm_model;

      const job = await createJob(
        jobType,
        file,
        undefined,
        false,
        undefined,
        currentSession?.id,
        llmProvider,
        llmModel
      );
      setLastJobId(job.id);

      const pendingJob: PendingJob = {
        jobId: job.id,
        jobNumber: String(job.job_number || job.id),
        imagePreview: imagePreviewUrl,
        status: "processing",
        sessionId: currentSession?.id,
      };
      setPendingJobs((prev) => [...prev, pendingJob]);
      startJobPolling(job.id, String(job.job_number || job.id), imagePreviewUrl);
    } catch (error: any) {
      await sendMessage(`Failed to create job: ${error.message}`, currentSession?.id);
    } finally {
      setUploading(false);
    }
  };

  const startJobPolling = async (jobId: string, jobNumber: string, imagePreview: string) => {
    try {
      await pollJobStatus(jobId, 3000, async (job) => {
        if (job.status === "completed") {
          try {
            const fullJob = await getJobById(jobId);
            setPendingJobs((prev) =>
              prev.map((pj) =>
                pj.jobId === jobId
                  ? {
                      ...pj,
                      status: "completed" as const,
                      jobData: {
                        id: fullJob.id,
                        job_number: fullJob.job_number,
                        job_type: fullJob.job_type,
                        status: fullJob.status,
                        input_preview: pj.imagePreview,
                        annotated_output_url: fullJob.annotated_output_url,
                        results: fullJob.results,
                        completed_at: fullJob.completed_at,
                      },
                    }
                  : pj
              )
            );
          } catch (fetchError) {
            setPendingJobs((prev) =>
              prev.map((pj) => (pj.jobId === jobId ? { ...pj, status: "completed" as const } : pj))
            );
          }
        } else if (job.status === "failed") {
          setPendingJobs((prev) =>
            prev.map((pj) =>
              pj.jobId === jobId
                ? { ...pj, status: "failed" as const, errorMessage: job.error_message || undefined }
                : pj
            )
          );
        }
      });
    } catch (error) {
      console.error("Polling error:", error);
    }
  };

  // Action cards for welcome/mode selection (matching mockup)
  const actionCards = [
    {
      mode: "analyze" as const,
      icon: <Video size={18} />,
      label: "Analyze my video",
    },
    {
      mode: "create" as const,
      icon: <FilePlus size={18} />,
      label: "Create a new job",
    },
    {
      mode: "query" as const,
      icon: <ClipboardList size={18} />,
      label: "Check job status",
    },
    {
      mode: "help" as const,
      icon: <Lightbulb size={18} />,
      label: "Explain a result",
    },
  ];

  // AI Avatar for consistency
  const AI_AVATAR = (
    <div className="w-10 h-10 rounded-full bg-[#1a2b4b] border border-blue-500/30 flex items-center justify-center">
      <Bot className="w-5 h-5 text-blue-400" />
    </div>
  );

  // Welcome screen (no session or no messages)
  if (!currentSession || messages.length === 0) {
    return (
      <div className="flex flex-col h-full bg-[#0a1120] overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-[#0a1120]/80 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">UniVision</h1>
              <p className="text-xs text-slate-400">AI Assistant</p>
            </div>
          </div>
          <button
            onClick={handleStartNewChat}
            className="flex items-center gap-2 bg-[#1d63ed] hover:bg-[#1652cc] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-blue-900/20"
          >
            <Plus size={18} />
            New Chat
          </button>
        </header>

        {/* Hero Section */}
        <main className="flex-1 flex items-center justify-center p-4 lg:p-6 overflow-hidden">
          <section className="relative overflow-hidden rounded-[24px] border border-white/10 bg-[#0f1a30] p-6 lg:p-10 text-center shadow-2xl max-w-2xl w-full">
            {/* Background Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent pointer-events-none" />
            
            <div className="relative z-10 flex flex-col items-center">
              {/* AI Avatar with Glow */}
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full scale-150" />
                <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center relative z-10 shadow-lg shadow-blue-500/30">
                  <Bot className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
                </div>
              </div>
              
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2 tracking-tight">
                How can I help you today?
              </h2>
              <p className="text-slate-400 mb-6 text-sm lg:text-base">
                Your intelligent assistant for visual analysis
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-center gap-3">
                {actionCards.map((card) => (
                  <ActionButton
                    key={card.mode}
                    icon={card.icon}
                    label={card.label}
                    onClick={() => handleModeSelect(card.mode)}
                  />
                ))}
              </div>
            </div>
          </section>
        </main>

        {/* Input Area */}
        <ChatInput
          onSendMessage={handleSendMessage}
          onUploadFile={handleUploadFile}
          disabled={uploading}
          sending={sending}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#0a1120]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-[#0a1120]/80 backdrop-blur-md">
        <div className="flex items-center gap-3">
          {AI_AVATAR}
          <div>
            <h2 className="text-white font-semibold">
              {currentSession?.title || "AI Assistant"}
            </h2>
            <p className="text-xs">
              {currentSession?.is_active === false ? (
                <span className="text-red-400">Conversation Closed</span>
              ) : sending ? (
                <span className="text-amber-400">Typing...</span>
              ) : (
                <span className="text-emerald-400">Online</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {currentSession?.is_active && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEndConversation}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              End Chat
            </Button>
          )}
          <button
            onClick={handleStartNewChat}
            className="flex items-center gap-2 bg-[#1d63ed] hover:bg-[#1652cc] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-blue-900/20"
          >
            <Plus size={18} />
            New Chat
          </button>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {messages.map((msg: any) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}

        {/* Pending Jobs */}
        {pendingJobs
          .filter((pj) => pj.sessionId === currentSession?.id)
          .map((pj) => (
            <div key={pj.jobId} className="space-y-4">
              {/* User uploaded image */}
              <div className="flex justify-end">
                <div className="max-w-[80%]">
                  <div className="bg-[#1a2b4b]/60 border border-[#2d4a7c] rounded-2xl rounded-tr-none p-4">
                    <img
                      src={pj.imagePreview}
                      alt="Uploaded"
                      className="rounded-xl max-h-48 object-cover"
                    />
                    <p className="text-xs text-slate-500 mt-2">Job #{pj.jobNumber}</p>
                  </div>
                </div>
              </div>

              {/* Job Result Card */}
              {pj.status === "completed" && pj.jobData && (
                <div className="flex gap-4">
                  <div className="flex-shrink-0">{AI_AVATAR}</div>
                  <div className="flex-1 max-w-[85%]">
                    <JobResultCard job={pj.jobData} showImages={true} />
                  </div>
                </div>
              )}

              {/* Processing indicator */}
              {pj.status === "processing" && (
                <div className="flex gap-4">
                  <div className="flex-shrink-0">{AI_AVATAR}</div>
                  <div className="bg-[#0f1a30] border border-white/5 rounded-2xl rounded-tl-none px-5 py-4">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                      <span className="text-sm text-slate-300">
                        Processing Job #{pj.jobNumber}...
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Failed indicator */}
              {pj.status === "failed" && (
                <div className="flex gap-4">
                  <div className="flex-shrink-0">{AI_AVATAR}</div>
                  <div className="bg-red-900/20 border border-red-500/30 rounded-2xl rounded-tl-none px-5 py-4">
                    <div className="flex items-center gap-2 text-red-400">
                      <XCircle className="w-4 h-4" />
                      <span className="text-sm">Job #{pj.jobNumber} failed</span>
                    </div>
                    {pj.errorMessage && (
                      <p className="text-xs text-red-400/70 mt-1">{pj.errorMessage}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

        {sending && <TypingIndicator />}
        <div ref={messagesEndRef} />

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        {uploading && (
          <div className="flex justify-end">
            <div className="max-w-[80%]">
              {uploadedImagePreview && (
                <div className="mb-2">
                  <img
                    src={uploadedImagePreview}
                    alt="Uploading"
                    className="rounded-xl max-h-48 object-cover opacity-70"
                  />
                </div>
              )}
              <div className="bg-[#1d63ed] text-white rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Creating job...</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      {currentSession?.is_active === false ? (
        <div className="p-6 bg-[#0f1a30] border-t border-white/5 text-center">
          <p className="text-slate-400 mb-2">This conversation has ended</p>
          <p className="text-slate-500 text-sm">
            You can view the history but cannot send new messages.
          </p>
        </div>
      ) : (
        <ChatInput
          onSendMessage={handleSendMessage}
          onUploadFile={handleUploadFile}
          disabled={uploading}
          sending={sending}
        />
      )}
    </div>
  );
};
